import { Capacitor } from '@capacitor/core';

/**
 * The origin to use for any URL that leaves the app — shared to WhatsApp/
 * SMS/email, copied to the clipboard, embedded in a QR code, etc.
 *
 * `window.location.origin` is NOT safe for this: on native it's expected to
 * resolve to the real `https://tof-box.app` origin per capacitor.config.json
 * (`iosScheme`/`androidScheme: "https"`), but in practice the installed app
 * has been observed producing links like `capacitor://tof-box.app/...` —
 * a scheme no other app can open, so the "link" a recipient gets is just
 * inert text. Hardcoding the real public domain here sidesteps that
 * platform quirk entirely, whatever its root cause.
 *
 * Do NOT use this for the Emergent OAuth redirect or the Stripe checkout
 * return URL — those must keep using `window.location.origin` as-is (see
 * the "DO NOT HARDCODE THE URL" warnings at their call sites).
 */
export const PUBLIC_APP_ORIGIN = Capacitor.isNativePlatform()
  ? 'https://tof-box.app'
  : window.location.origin;

// Public pages a shared link can lead to — mirrors PAGES in
// backend/link_preview_routes.py.
const SHARED_PAGES = {
  s: '/s/', f: '/f/', u: '/u/', poll: '/poll/', santa: '/santa/', parking: '/parking/',
  meet: '/meet/', pioche: '/pioche/', family: '/family-invite/', project: '/projects/invite/',
  ref: '/register?ref=', countdown: '/countdown/', tirage: '/tirage/',
};

/**
 * The link to give out for one of the app's public pages (copied, shared,
 * in a QR code). It goes through the server's link preview (/api/p, see
 * backend/link_preview_routes.py), so WhatsApp, Messenger, X… show what is
 * shared, in the sharer's language — then it leads to the page itself.
 */
export const sharedLink = (kind, token) =>
  `${PUBLIC_APP_ORIGIN}/api/p/${kind}/${encodeURIComponent(token)}?l=${document.documentElement.lang || 'en'}`;

/** The page a /api/p/<kind>/<token> link leads to (links opened in the app). */
export const sharedLinkPage = (pathname) => {
  const m = /^\/api\/p\/([a-z]+)\/([^/]+)\/?$/.exec(pathname);
  return m && SHARED_PAGES[m[1]] ? `${SHARED_PAGES[m[1]]}${m[2]}` : null;
};
